import { ChangeDetectionStrategy, Component, ComponentRef, Input } from '@angular/core';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { IsfModel } from '@data-layer/models/isf';
import { InternalNotesComponent } from '@shared/internal-notes/components';
import { takeUntil } from 'rxjs/operators';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { AppService } from '@app/app.service';
import { EntrySummaryRepository, IsfRepository } from '@data-layer/repositories';
import { Observable, ReplaySubject } from 'rxjs';
import { AppLayoutService } from '@app/modules/app-layout/app-layout.service';
import { EnumHintKey } from '@data-layer/enums/common';

@Component({
  selector: 'internal-notes-card',
  templateUrl: './internal-notes-card.component.html',
  styleUrls: ['./internal-notes-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalNotesCardComponent {
  @Input() public product: EntrySummaryModel | IsfModel;
  @Input() public isIsfPlacement: boolean;

  public enumHintKey: typeof EnumHintKey = EnumHintKey;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private appLayoutService: AppLayoutService,
    private appService: AppService,
    private entrySummaryRepository: EntrySummaryRepository,
    private isfRepository: IsfRepository,
  ) {}

  public openInternalNotes(): void {
    const componentSidebarRef: ComponentRef<InternalNotesComponent> = this.appService.openSidebar(InternalNotesComponent, {
      sidebarHeaderText: !!this.product.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT' : 'LABELS.INTERNAL_NOTES_ADD',
      tooltipText: !!this.product.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT_DESCRIPTION' : 'LABELS.INTERNAL_NOTES_ADD_DESCRIPTION',
    });
    componentSidebarRef.instance.internalNote = {
      isIsfPlacement: this.isIsfPlacement,
      id: this.product.id,
      internalNotes: this.product.internalNotes,
      internalReferenceNumber: this.product.internalReferenceNumber,
    };
    componentSidebarRef.instance.saveInternalNotes.pipe(takeUntil(this.destroy$)).subscribe((data: InternalNote) => {
      (this.isIsfPlacement ? this.updateIsfInternalNotes(data) : this.updateEsInternalNotes(data)).subscribe(() => {
        this.appLayoutService.internalNotes$.next({
          isIsfPlacement: this.isIsfPlacement,
          id: this.product.id,
          internalNotes: data.internalNotes,
          internalReferenceNumber: data.internalReferenceNumber,
        });
        this.appLayoutService.internalNotesUpdated$.next();
        this.appService.closeSidebar();
      });
    });
  }

  public updateIsfInternalNotes(data: InternalNote): Observable<void> {
    return this.isfRepository.updateInternalNote(this.product.id, data).pipe(takeUntil(this.destroy$));
  }

  public updateEsInternalNotes(data: InternalNote): Observable<void> {
    return this.entrySummaryRepository.updateInternalNote(this.product.id, data).pipe(takeUntil(this.destroy$));
  }
}
